import { Localized, LanguageSelector } from '../../i18n/I18n';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, Link2, Menu, ReceiptText, UserRound, X } from 'lucide-react';
import { serviceCatalog, serviceGroups } from '../../data/serviceCatalog';
import { serviceVisuals } from '../../data/serviceVisuals';
import './navigation.css';
import BrandLogo from './BrandLogo';

const navigationGroups = [
  { title: serviceGroups[0], href: '/servicios#categoria-0', slugs: ['automatizacion-ia', 'integraciones'] },
  { title: serviceGroups[1], href: '/servicios#categoria-1', slugs: ['desarrollo-web', 'despliegue-aplicaciones', 'vps-cloud'] },
  { title: serviceGroups[2], href: '/servicios#categoria-2', slugs: ['soporte-computo', 'seguridad-informatica', 'redes-datos'] },
  { title: 'Equipos y software', href: '/servicios#categoria-3', slugs: ['hardware', 'licencias-software'] },
  { title: 'Tu marca', href: '/servicios/registro-marca', slugs: ['registro-marca'] },
];

export default function SiteHeader() {
  const [panel, setPanel] = useState(null);
  const header = useRef(null);
  const servicesButton = useRef(null);
  const accountButton = useRef(null);
  const menuButton = useRef(null);
  const hoverTimer = useRef(null);
  const focusPanel = useRef(null);
  const servicesOpen = panel === 'services';
  const accountOpen = panel === 'account';
  const mobileOpen = panel === 'mobile';
  const cancelHover = () => clearTimeout(hoverTimer.current);
  const close = () => { cancelHover(); setPanel(null); };
  const toggle = name => { cancelHover(); setPanel(value => value === name ? null : name); };
  const openFromKeyboard = (event, name) => {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    cancelHover();
    focusPanel.current = name;
    setPanel(name);
    // An already open disclosure has no state change to trigger the effect.
    if (panel === name) {
      header.current?.querySelector(name === 'services' ? '#bz-services-menu a' : '#bz-account-menu a')?.focus();
      focusPanel.current = null;
    }
  };
  // Pointer intent opens only on hover-capable devices; touch retains click.
  const hoverOpen = (event, name) => {
    cancelHover();
    if (event.pointerType === 'mouse' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
      hoverTimer.current = setTimeout(() => setPanel(name), 140);
    }
  };
  const hoverClose = event => {
    if (event.pointerType !== 'mouse') return;
    if (header.current?.querySelector('#bz-services-menu, #bz-account-menu')?.contains(document.activeElement)) return;
    cancelHover();
    // A forgiving delay keeps the route from trigger to full-width panel open.
    hoverTimer.current = setTimeout(() => setPanel(null), 220);
  };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  useEffect(() => {
    if (focusPanel.current !== panel || !panel) return;
    header.current?.querySelector(panel === 'services' ? '#bz-services-menu a' : '#bz-account-menu a')?.focus();
    focusPanel.current = null;
  }, [panel]);
  useEffect(() => {
    if (!panel) return undefined;
    const onKey = event => {
      if (event.key !== 'Escape') return;
      clearTimeout(hoverTimer.current);
      setPanel(null);
      ({ services: servicesButton, account: accountButton, mobile: menuButton })[panel].current?.focus();
    };
    const onOutside = event => {
      if (!header.current?.contains(event.target)) { clearTimeout(hoverTimer.current); setPanel(null); }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onOutside);
    document.addEventListener('focusin', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onOutside);
      document.removeEventListener('focusin', onOutside);
    };
  }, [panel]);
  useEffect(() => {
    const query = window.matchMedia?.('(max-width: 980px)');
    const reset = () => { clearTimeout(hoverTimer.current); setPanel(null); };
    query?.addEventListener?.('change', reset);
    return () => query?.removeEventListener?.('change', reset);
  }, []);

  const renderServiceLink = slug => {
    const service = serviceCatalog.find(item => item.slug === slug);
    const ServiceIcon = serviceVisuals[slug].icon;
    return <a key={slug} className="bz-service-menu-link" href={'/servicios/' + slug} onClick={close}>
      <ServiceIcon className="bz-menu-option-icon" size={18} strokeWidth={1.7} aria-hidden="true"/>
      <span className="bz-menu-option-label">{service.title}<span className="bz-menu-chevron-slot" aria-hidden="true"><ChevronRight className="bz-menu-option-chevron" size={13} strokeWidth={2.8}/></span></span>
    </a>;
  };

  return <Localized><header ref={header} className="bz-header bz-header-v2" onPointerEnter={cancelHover} onPointerLeave={hoverClose}>
    <div className="bz-shell bz-nav">
      <a href="/#hero" className="bz-brand" aria-label="Bezalel Technology — Inicio" onClick={close}><BrandLogo/></a>
      <nav className="bz-desktop-nav" aria-label="Navegación principal">
        <button ref={servicesButton} type="button" className="bz-services-toggle" aria-expanded={servicesOpen} aria-controls="bz-services-menu" onClick={() => toggle('services')} onPointerEnter={event => hoverOpen(event, 'services')} onKeyDown={event => openFromKeyboard(event, 'services')}>Servicios <ChevronDown size={12} strokeWidth={3} aria-hidden="true"/></button>
        <a href="/#en-accion" onClick={close} onPointerEnter={close}>IA en acción</a>
        <a href="/#about" onClick={close} onPointerEnter={close}>Nosotros</a>
        <a href="/noticias" onClick={close} onPointerEnter={close}>Noticias</a>
      </nav>
      <div className="bz-header-actions">
        <LanguageSelector/>
        <a className="bz-header-contact" href="/#contacto" onClick={close} onPointerEnter={close}>Contacto</a>
        <div className="bz-account-disclosure">
          <button ref={accountButton} className="bz-account-toggle" type="button" aria-label="Area de clientes" title="Area de clientes" aria-expanded={accountOpen} aria-controls="bz-account-menu" onClick={() => toggle('account')} onPointerEnter={event => hoverOpen(event, 'account')} onKeyDown={event => openFromKeyboard(event, 'account')}><UserRound size={24} strokeWidth={1.7} aria-hidden="true"/><ChevronDown size={13} aria-hidden="true"/></button>
          {accountOpen && <nav id="bz-account-menu" className="bz-account-menu" aria-label="Accesos de clientes" onPointerEnter={cancelHover}>
            <p>Area de clientes</p>
            <button type="button" disabled><UserRound size={17} strokeWidth={1.7} aria-hidden="true"/>Ingresar a mi cuenta</button>
            <button type="button" disabled><ReceiptText size={17} strokeWidth={1.7} aria-hidden="true"/>Consultar facturación</button>
            <button type="button" disabled><Link2 size={17} strokeWidth={1.7} aria-hidden="true"/>Acortador de enlaces</button>
            <small className="bz-client-pending-note">Próximamente disponibles.</small>
          </nav>}
        </div>
      </div>
      <button ref={menuButton} type="button" className="bz-menu-button" aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={mobileOpen} aria-controls="bz-mobile-nav" onClick={() => toggle('mobile')}>{mobileOpen ? <X size={20} aria-hidden="true"/> : <Menu size={20} aria-hidden="true"/>}<span>Menú</span></button>
    </div>
    {servicesOpen && <nav id="bz-services-menu" className="bz-mega-menu" aria-label="Servicios de Bezalel" onPointerEnter={cancelHover}>
      <div className="bz-mega-groups">{navigationGroups.map(group => <section className="bz-nav-column" key={group.title}>
        <h2><a href={group.href} onClick={close}>{group.title}<span className="bz-menu-chevron-slot" aria-hidden="true"><ChevronRight className="bz-category-chevron" size={14} strokeWidth={3}/></span></a></h2>
        <ul>{group.slugs.map(slug => <li key={slug}>{renderServiceLink(slug)}</li>)}</ul>
      </section>)}</div>
      <div className="bz-mega-footer"><a href="/servicios" onClick={close}>Todos los servicios <ChevronRight size={14} strokeWidth={2.8} aria-hidden="true"/></a><a href="/#soporte-empresarial" onClick={close}>Soporte para tu empresa <ChevronRight size={14} strokeWidth={2.8} aria-hidden="true"/></a></div>
    </nav>}
    {mobileOpen && <nav id="bz-mobile-nav" className="bz-mobile-nav" aria-label="Navegación móvil">
      <a href="/servicios" onClick={close}>Todos los servicios <ChevronRight size={14} strokeWidth={2.8} aria-hidden="true"/></a>
      {navigationGroups.map(group => <details className="bz-mobile-services" key={group.title}><summary>{group.title}<ChevronDown size={14} strokeWidth={2.8} aria-hidden="true"/></summary>{group.slugs.map(renderServiceLink)}</details>)}
      <div className="bz-mobile-quick-links">{[['IA en acción','/#en-accion'],['Soporte empresarial','/#soporte-empresarial'],['Nosotros','/#about'],['Noticias','/noticias'],['Contacto','/#contacto']].map(([name, href]) => <a key={href} href={href} onClick={close}>{name}<ChevronRight size={14} strokeWidth={2.8} aria-hidden="true"/></a>)}<button type="button" className="bz-mobile-client-pending" disabled>Area de clientes · Próximamente</button></div>
    </nav>}
  </header></Localized>;
}
